import { useEffect, useState } from 'react';

/**
 * Knowing when the code in this tab is older than the code on the server.
 *
 * Pages are never cached and every asset name carries a content hash, so a fresh load is
 * always current. What goes stale is a tab left open across a deploy: it keeps running
 * the old bundle, and the first time it asks for a file loaded on demand — a route, the
 * PDF worker — that file no longer exists. People learned to work around it with private
 * windows. This makes the tab notice instead.
 *
 * `current` is the release this bundle was built as (the build stamps it); `/version.json`
 * is the release the server is serving, and is never cached.
 */
export function useReleaseCheck(current: string, intervalMs = 60_000): { outdated: boolean; latest: string | null } {
  const [latest, setLatest] = useState<string | null>(null);

  useEffect(() => {
    // A development build has no release to compare.
    if (!current || current === 'dev') return;
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const body = (await res.json()) as { release?: string };
        if (!cancelled && body.release) setLatest(body.release);
      } catch {
        // Offline or mid-deploy: try again next time.
      }
    };
    void check();
    const timer = setInterval(check, intervalMs);
    const onVisible = () => document.visibilityState === 'visible' && void check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [current, intervalMs]);

  return { outdated: Boolean(latest && current !== 'dev' && latest !== current), latest };
}

/** The non-blocking notice. Reloading is the person's choice — they may be mid-form. */
export function ReleaseBanner({ latest }: { latest: string | null }) {
  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-4 z-50 mx-auto flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-center gap-3 rounded-[var(--pv-radius)] border border-[var(--pv-line-strong)] bg-[var(--pv-card)] px-4 py-2.5 shadow-[var(--pv-shadow-lg)] [font-size:13.5px]"
    >
      <span>
        Provenir has been updated{latest ? ` (release ${latest})` : ''}. Reload to use the new version.
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-[var(--pv-radius-sm)] bg-[var(--pv-brand)] px-3 py-1 font-medium text-[var(--pv-brand-on)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--pv-brand)]"
      >
        Reload
      </button>
    </div>
  );
}

/**
 * The safety net: a file this tab needs was removed by a deploy. Reload once to pick up
 * the new release; the session flag stops a genuinely missing file from looping.
 */
export function installChunkReloadGuard(current: string): void {
  window.addEventListener('vite:preloadError', (event) => {
    let alreadyTried = false;
    try {
      alreadyTried = sessionStorage.getItem('pv-reloaded-for') === current;
      sessionStorage.setItem('pv-reloaded-for', current);
    } catch {
      // Storage blocked: reload anyway; the browser's own history stops a tight loop.
    }
    if (alreadyTried) return;
    event.preventDefault();
    window.location.reload();
  });
}
